import type {
  ReportFindingSection,
  ReportVerificationReport,
  ReportVerificationStaffMember,
} from '../types/reportVerification.types';

export function formatPatientDisplay(report: ReportVerificationReport): string {
  return (
    report.patient_name?.trim() ||
    report.patient?.full_name?.trim() ||
    report.patient?.name?.trim() ||
    ''
  );
}

export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

export function formatStatusBadge(status?: string, version?: string): string | null {
  if (!status && !version) {
    return null;
  }

  if (status && version) {
    const normalizedVersion = version.replace(/\.0+$/, '') || '1';
    return `${status} · v${normalizedVersion}`;
  }

  if (status) {
    return status;
  }

  const normalizedVersion = version?.replace(/\.0+$/, '') || '1';
  return `v${normalizedVersion}`;
}

export function formatRegistrationNo(registrationNo?: string): string | null {
  if (!registrationNo?.trim()) {
    return null;
  }

  const trimmed = registrationNo.trim();
  if (trimmed === '...' || trimmed === '—' || trimmed === '-') {
    return null;
  }

  return trimmed;
}

export function buildReportedByList(
  report: ReportVerificationReport
): ReportVerificationStaffMember[] {
  if (report.reported_by?.length) {
    return report.reported_by.map(member => ({
      ...member,
      registration_no: formatRegistrationNo(member.registration_no) || undefined,
      initials: member.initials || getInitials(member.name),
    }));
  }

  if (report.reporting_radiologist) {
    return [
      {
        name: report.reporting_radiologist,
        role: 'Reporting Radiologist',
        registration_no: report.medical_registration_no,
        initials: getInitials(report.reporting_radiologist),
      },
    ];
  }

  return [];
}

export function normalizeFindings(findings?: ReportFindingSection[]): ReportFindingSection[] {
  if (!findings?.length) {
    return [];
  }

  return findings.filter(section => section.title?.trim() || section.content?.trim());
}

export function normalizeImpression(impression?: string[]): string[] {
  if (!impression?.length) {
    return [];
  }

  return impression.map(item => item.trim()).filter(Boolean);
}

export function hasReportContent(report: ReportVerificationReport): boolean {
  return Boolean(
    report.clinical_history?.trim() ||
      report.technique?.trim() ||
      normalizeFindings(report.findings).length > 0 ||
      normalizeImpression(report.impression).length > 0
  );
}

export function isVerifiedStatus(status?: string): boolean {
  return status?.toUpperCase() === 'VERIFIED';
}
